import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Select from 'react-select';
import moment from 'moment';
import { toast } from 'sonner';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { CalendarCheck, CalendarX2, HelpCircle } from 'lucide-react';

import { brandSelectTheme } from '../Common/reactSelectTheme';
import EmptyState from '../Common/EmptyState';
import * as YearEndService from '../../../services/YearEndService';

import YearEndProgressTracker from './components/YearEndProgressTracker';
import YearEndIntroCard from './components/YearEndIntroCard';
import PreflightStep from './steps/PreflightStep';
import ReconciliationStep from './steps/ReconciliationStep';
import AdjustmentsStep from './steps/AdjustmentsStep';
import TaxStep from './steps/TaxStep';
import AnnualReportStep from './steps/AnnualReportStep';
import SignaturesStammaStep from './steps/SignaturesStammaStep';
import PostStammaStep from './steps/PostStammaStep';
import DeklarationStep from './steps/DeklarationStep';
import EfResultStep from './steps/EfResultStep';
import NeDeklarationStep from './steps/NeDeklarationStep';
import HbResultStep from './steps/HbResultStep';
import Ink4DeklarationStep from './steps/Ink4DeklarationStep';
import StatementsPanel from './components/StatementsPanel';
import PlaceholderStep from './steps/PlaceholderStep';
import BolagsverketStep from './steps/BolagsverketStep';
import {
    StepEnum, STEP_ORDER, stepTitle,
    stepPhase, PhaseEnum, PHASE_TITLES, PHASE_DESCRIPTIONS, PHASE_INTROS,
} from './constants';

const STATUS_LABELS = {
    not_started: 'Inte påbörjat',
    in_progress: 'Pågår',
    awaiting_signatures: 'Väntar på underskrifter',
    awaiting_stamma: 'Väntar på årsstämma',
    filed_bolagsverket: 'Inskickad till Bolagsverket',
    filed_skatteverket: 'Deklaration inlämnad',
    completed: 'Klart',
};

/** Badge tint per process status — the VatModernHeader badge palette. */
const STATUS_VARIANTS = {
    not_started: 'neutral',
    in_progress: 'primary',
    awaiting_signatures: 'warning',
    awaiting_stamma: 'warning',
    filed_bolagsverket: 'primary',
    filed_skatteverket: 'primary',
    completed: 'success',
};

/**
 * Bokslut (year-end) — route /:companyId/year-end[/:fiscalYearId].
 *
 * Landing: pick the most recently ended fiscal year (selector to
 * switch); no process yet → intro card with "Starta bokslut".
 * With a process: VatPeriodStepper-style wizard shell driven by the
 * backend's persisted current_step + per-step completion flags. Only
 * the steps in the overview payload's `steps` array are shown (an EF
 * process has 5 of the 9); P1 makes steps 1–2 functional and renders
 * the rest as locked placeholders.
 */
function YearEndPage() {
    const { companyId, fiscalYearId: fyParam } = useParams();
    const history = useHistory();

    const [fiscalYears, setFiscalYears] = useState([]);
    const [loadingYears, setLoadingYears] = useState(true);
    const [yearsError, setYearsError] = useState(null);

    const [overview, setOverview] = useState(null);
    const [loadingOverview, setLoadingOverview] = useState(false);
    const [overviewError, setOverviewError] = useState(null); // { message, retryable }
    const [forbidden, setForbidden] = useState(false);

    const [starting, setStarting] = useState(false);
    const [viewingStep, setViewingStep] = useState(null);
    const [stepBusy, setStepBusy] = useState(false);
    const [gateError, setGateError] = useState(null); // { step, message, failingChecks }
    const [runningChecks, setRunningChecks] = useState(false);
    const [refreshingChecklist, setRefreshingChecklist] = useState(false);

    // ---- Fiscal years -----------------------------------------------------

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await YearEndService.listFiscalYears(companyId);
                if (cancelled) return;
                const years = (data?.fiscalYears || [])
                    .slice()
                    .sort((a, b) => new Date(b.end_date) - new Date(a.end_date));
                setFiscalYears(years);
            } catch (err) {
                if (!cancelled) setYearsError('Kunde inte hämta räkenskapsåren. Försök igen senare.');
            } finally {
                if (!cancelled) setLoadingYears(false);
            }
        })();
        return () => { cancelled = true; };
    }, [companyId]);

    // Default: the most recently ENDED fiscal year — that's the one a
    // bokslut is normally made for. Reflect the choice in the URL so the
    // page is deep-linkable.
    useEffect(() => {
        if (loadingYears || fiscalYears.length === 0 || fyParam) return;
        const today = moment();
        const ended = fiscalYears.find((fy) => moment(fy.end_date).isBefore(today, 'day'));
        const target = ended || fiscalYears[0];
        const id = target.fiscal_year_id ?? target.id;
        history.replace(`/${companyId}/year-end/${id}`);
    }, [loadingYears, fiscalYears, fyParam, companyId, history]);

    const selectedFiscalYear = useMemo(
        () => fiscalYears.find((fy) => String(fy.fiscal_year_id ?? fy.id) === String(fyParam)) || null,
        [fiscalYears, fyParam]
    );

    const yearOptions = useMemo(() => fiscalYears.map((fy) => ({
        value: fy.fiscal_year_id ?? fy.id,
        label: `${fy.year} (${moment(fy.start_date).format('YYYY-MM-DD')} – ${moment(fy.end_date).format('YYYY-MM-DD')})`,
    })), [fiscalYears]);

    const selectedOption = yearOptions.find((o) => String(o.value) === String(fyParam)) || null;

    // ---- Overview ---------------------------------------------------------

    const fetchOverview = useCallback(async ({ silent = false } = {}) => {
        if (!fyParam) return;
        if (!silent) setLoadingOverview(true);
        setOverviewError(null);
        setForbidden(false);
        try {
            const resp = await YearEndService.getOverview(companyId, fyParam);
            setOverview(resp?.data || null);
        } catch (err) {
            if (err.response?.status === 403) {
                setForbidden(true);
            } else {
                // 4xx messages are written for the user (unsupported year,
                // K3 out of scope …) and retrying won't change the answer.
                // 5xx bodies carry internal English fallbacks — never show
                // those; a generic message + retry is the honest offer.
                const businessMessage = err.response?.status >= 400 && err.response?.status < 500
                    ? err.response?.data?.message
                    : null;
                setOverviewError({
                    message: businessMessage
                        || 'Bokslutet kunde inte hämtas just nu. Försök igen om en liten stund.',
                    // The longer why (e.g. why a historical year is refused)
                    // — shown behind a hover icon, not as body text.
                    explanation: businessMessage
                        ? err.response?.data?.details?.explanation || null
                        : null,
                    retryable: !businessMessage,
                });
            }
        } finally {
            setLoadingOverview(false);
        }
    }, [companyId, fyParam]);

    useEffect(() => {
        setOverview(null);
        setViewingStep(null);
        setGateError(null);
        fetchOverview();
    }, [fetchOverview]);

    const process = overview?.process || null;

    // Steps shown = the overview payload's `steps` array (EF has 5).
    const shownSteps = useMemo(() => {
        const s = overview?.steps;
        if (Array.isArray(s) && s.length > 0) return s;
        return STEP_ORDER;
    }, [overview]);

    const currentStep = process?.current_step && shownSteps.includes(process.current_step)
        ? process.current_step
        : shownSteps[0];
    const displayedStep = viewingStep && shownSteps.includes(viewingStep) ? viewingStep : currentStep;
    const isPreviewing = displayedStep !== currentStep;

    const isStepCompleted = useCallback(
        (step) => Boolean(process?.[`${step}_completed`]),
        [process]
    );

    // ---- Actions ----------------------------------------------------------

    const handleStart = async (eligibilityAssessment) => {
        setStarting(true);
        try {
            await YearEndService.startProcess(companyId, fyParam, eligibilityAssessment);
            toast.success('Bokslutet har startats');
            await fetchOverview({ silent: true });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte starta bokslutet.');
        } finally {
            setStarting(false);
        }
    };

    const handleRunChecks = useCallback(async () => {
        setRunningChecks(true);
        try {
            const resp = await YearEndService.getReadiness(companyId, fyParam);
            setOverview((prev) => (prev ? { ...prev, readiness: resp?.data } : prev));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte köra kontrollerna.');
        } finally {
            setRunningChecks(false);
        }
    }, [companyId, fyParam]);

    const handleCompleteStep = async (step) => {
        if (!process) return;
        setStepBusy(true);
        setGateError(null);
        try {
            await YearEndService.completeStep(companyId, process.process_id, step);
            toast.success(`Steget ”${stepTitle(step, process?.process_type)}” är markerat som klart`);
            await fetchOverview({ silent: true });
        } catch (err) {
            const data = err.response?.data;
            if (err.response?.status === 409) {
                const details = data?.details;
                let failingChecks = null;
                if (Array.isArray(details)) {
                    failingChecks = details;
                } else if (Array.isArray(details?.checks)) {
                    failingChecks = details.checks.filter((c) => c.status === 'fail' || c.status === 'warn');
                } else if (Array.isArray(details?.failing)) {
                    failingChecks = details.failing;
                }
                setGateError({
                    step,
                    message: data?.message || 'Steget kan inte markeras som klart ännu.',
                    failingChecks,
                });
                toast.error(data?.message || 'Steget kan inte markeras som klart ännu.');
            } else {
                toast.error(data?.message || 'Kunde inte markera steget som klart.');
            }
        } finally {
            setStepBusy(false);
        }
    };

    const handleUndoStep = async (step) => {
        if (!process) return;
        setStepBusy(true);
        setGateError(null);
        try {
            await YearEndService.undoCompleteStep(companyId, process.process_id, step);
            toast.success('Markeringen är borttagen');
            await fetchOverview({ silent: true });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte ångra steget.');
        } finally {
            setStepBusy(false);
        }
    };

    const handleRefreshChecklist = useCallback(async () => {
        if (!process) return;
        setRefreshingChecklist(true);
        try {
            const resp = await YearEndService.refreshChecklist(companyId, process.process_id);
            const items = Array.isArray(resp?.data) ? resp.data : [];
            setOverview((prev) => (prev ? { ...prev, checklist: items } : prev));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte uppdatera kontolistan.');
        } finally {
            setRefreshingChecklist(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, process?.process_id]);

    const patchChecklistItem = (itemId, patch) => {
        setOverview((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                checklist: (prev.checklist || []).map((item) =>
                    item.item_id === itemId ? { ...item, ...patch } : item
                ),
            };
        });
    };

    const handleAttest = async (itemId, note) => {
        try {
            await YearEndService.attestChecklistItem(companyId, itemId, note);
            patchChecklistItem(itemId, {
                attested: 1,
                attested_at: new Date().toISOString(),
                note: note || null,
            });
            setGateError(null);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte attestera kontot.');
        }
    };

    const handleUnattest = async (itemId) => {
        try {
            await YearEndService.unattestChecklistItem(companyId, itemId);
            patchChecklistItem(itemId, { attested: 0, attested_at: null });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Kunde inte ta bort attesten.');
        }
    };

    // ---- Rendering --------------------------------------------------------

    const renderStepContent = () => {
        const isEnskildFirma = String(process?.process_type || '').startsWith('ef_');
        // Handelsbolag/kommanditbolag: eget resultatsteg (fördelning på
        // delägarna, ingen bolagsskatt) och egen deklaration (INK4).
        const isHandelsbolag = process?.process_type === 'hb_arsbokslut';
        if (displayedStep === StepEnum.PREFLIGHT) {
            return (
                <PreflightStep
                    companyId={companyId}
                    readiness={overview?.readiness}
                    onRunChecks={handleRunChecks}
                    runningChecks={runningChecks}
                    completed={isStepCompleted(StepEnum.PREFLIGHT)}
                    onComplete={() => handleCompleteStep(StepEnum.PREFLIGHT)}
                    onUndo={() => handleUndoStep(StepEnum.PREFLIGHT)}
                    busy={stepBusy}
                    gateError={gateError?.step === StepEnum.PREFLIGHT ? gateError : null}
                />
            );
        }
        if (displayedStep === StepEnum.RECONCILIATION) {
            return (
                <ReconciliationStep
                    items={overview?.checklist}
                    processId={process?.process_id}
                    companyId={companyId}
                    fiscalYearId={fyParam}
                    onRefresh={handleRefreshChecklist}
                    refreshing={refreshingChecklist}
                    onAttest={handleAttest}
                    onUnattest={handleUnattest}
                    completed={isStepCompleted(StepEnum.RECONCILIATION)}
                    onComplete={() => handleCompleteStep(StepEnum.RECONCILIATION)}
                    onUndo={() => handleUndoStep(StepEnum.RECONCILIATION)}
                    busy={stepBusy}
                    gateError={gateError?.step === StepEnum.RECONCILIATION ? gateError : null}
                />
            );
        }
        if (displayedStep === StepEnum.ADJUSTMENTS) {
            return (
                <AdjustmentsStep
                    companyId={companyId}
                    processId={process?.process_id}
                    completed={isStepCompleted(StepEnum.ADJUSTMENTS)}
                    onComplete={() => handleCompleteStep(StepEnum.ADJUSTMENTS)}
                    onUndo={() => handleUndoStep(StepEnum.ADJUSTMENTS)}
                    busy={stepBusy}
                    gateError={gateError?.step === StepEnum.ADJUSTMENTS ? gateError : null}
                />
            );
        }
        if (displayedStep === StepEnum.TAX) {
            // Enskild firma har ingen bolagsskatt och inga bokförda
            // dispositioner — steget för över årets resultat till 2019.
            if (isHandelsbolag) {
                return (
                    <HbResultStep
                        companyId={companyId}
                        processId={process?.process_id}
                        fiscalYearId={fyParam}
                        completed={isStepCompleted(StepEnum.TAX)}
                        onComplete={() => handleCompleteStep(StepEnum.TAX)}
                        onUndo={() => handleUndoStep(StepEnum.TAX)}
                        busy={stepBusy}
                        gateError={gateError?.step === StepEnum.TAX ? gateError : null}
                    />
                );
            }
            if (isEnskildFirma) {
                return (
                    <EfResultStep
                        companyId={companyId}
                        processId={process?.process_id}
                        completed={isStepCompleted(StepEnum.TAX)}
                        onComplete={() => handleCompleteStep(StepEnum.TAX)}
                        onUndo={() => handleUndoStep(StepEnum.TAX)}
                        busy={stepBusy}
                        gateError={gateError?.step === StepEnum.TAX ? gateError : null}
                    />
                );
            }
            return (
                <TaxStep
                    companyId={companyId}
                    processId={process?.process_id}
                    completed={isStepCompleted(StepEnum.TAX)}
                    onComplete={() => handleCompleteStep(StepEnum.TAX)}
                    onUndo={() => handleUndoStep(StepEnum.TAX)}
                    busy={stepBusy}
                    gateError={gateError?.step === StepEnum.TAX ? gateError : null}
                />
            );
        }
        if (displayedStep === StepEnum.ANNUAL_REPORT) {
            return (
                <AnnualReportStep
                    companyId={companyId}
                    processId={process?.process_id}
                    completed={isStepCompleted(StepEnum.ANNUAL_REPORT)}
                    onComplete={() => handleCompleteStep(StepEnum.ANNUAL_REPORT)}
                    onUndo={() => handleUndoStep(StepEnum.ANNUAL_REPORT)}
                    busy={stepBusy}
                    gateError={gateError?.step === StepEnum.ANNUAL_REPORT ? gateError : null}
                />
            );
        }
        if (displayedStep === StepEnum.SIGNATURES_STAMMA) {
            return (
                <SignaturesStammaStep
                    companyId={companyId}
                    processId={process?.process_id}
                    completed={isStepCompleted(StepEnum.SIGNATURES_STAMMA)}
                    onComplete={() => handleCompleteStep(StepEnum.SIGNATURES_STAMMA)}
                    onUndo={() => handleUndoStep(StepEnum.SIGNATURES_STAMMA)}
                    busy={stepBusy}
                    gateError={gateError?.step === StepEnum.SIGNATURES_STAMMA ? gateError : null}
                />
            );
        }
        if (displayedStep === StepEnum.DEKLARATION) {
            if (isHandelsbolag) {
                return (
                    <Ink4DeklarationStep
                        companyId={companyId}
                        processId={process?.process_id}
                        completed={isStepCompleted(StepEnum.DEKLARATION)}
                        onComplete={() => handleCompleteStep(StepEnum.DEKLARATION)}
                        onUndo={() => handleUndoStep(StepEnum.DEKLARATION)}
                        busy={stepBusy}
                        gateError={gateError?.step === StepEnum.DEKLARATION ? gateError : null}
                    />
                );
            }
            if (isEnskildFirma) {
                return (
                    <NeDeklarationStep
                        companyId={companyId}
                        processId={process?.process_id}
                        completed={isStepCompleted(StepEnum.DEKLARATION)}
                        onComplete={() => handleCompleteStep(StepEnum.DEKLARATION)}
                        onUndo={() => handleUndoStep(StepEnum.DEKLARATION)}
                        busy={stepBusy}
                        gateError={gateError?.step === StepEnum.DEKLARATION ? gateError : null}
                    />
                );
            }
            return (
                <DeklarationStep
                    companyId={companyId}
                    processId={process?.process_id}
                    completed={isStepCompleted(StepEnum.DEKLARATION)}
                    onComplete={() => handleCompleteStep(StepEnum.DEKLARATION)}
                    onUndo={() => handleUndoStep(StepEnum.DEKLARATION)}
                    busy={stepBusy}
                    gateError={gateError?.step === StepEnum.DEKLARATION ? gateError : null}
                />
            );
        }
        if (displayedStep === StepEnum.BOLAGSVERKET) {
            return (
                <BolagsverketStep
                    companyId={companyId}
                    processId={process?.process_id}
                    completed={isStepCompleted(StepEnum.BOLAGSVERKET)}
                    onComplete={() => handleCompleteStep(StepEnum.BOLAGSVERKET)}
                    onUndo={() => handleUndoStep(StepEnum.BOLAGSVERKET)}
                    busy={stepBusy}
                    gateError={gateError?.step === StepEnum.BOLAGSVERKET ? gateError : null}
                />
            );
        }
        if (displayedStep === StepEnum.POST_STAMMA) {
            return (
                <PostStammaStep
                    companyId={companyId}
                    processId={process?.process_id}
                    completed={isStepCompleted(StepEnum.POST_STAMMA)}
                    onComplete={() => handleCompleteStep(StepEnum.POST_STAMMA)}
                    onUndo={() => handleUndoStep(StepEnum.POST_STAMMA)}
                    busy={stepBusy}
                    gateError={gateError?.step === StepEnum.POST_STAMMA ? gateError : null}
                />
            );
        }
        return <PlaceholderStep step={displayedStep} />;
    };

    const loading = loadingYears || (loadingOverview && !overview);

    return (
        <>
            {/* Shared year-end chrome — consumed by the step components below,
                the same way VatStepContent's <style> serves the VAT steps.
                Cards/tables come from the compiled modern-card classes
                (src/scss/components/_modern-card.scss); only the year-end
                additions live here. */}
            <style>{`
                .ye-page-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 1.5rem 1rem;
                }

                /* modern-card-header modifier for step headers: long novice
                   copy + header actions need wrap + top alignment. */
                .ye-step-header {
                    flex-wrap: wrap;
                    gap: 0.75rem;
                    align-items: flex-start;
                }
                .ye-step-header .section-subtitle { max-width: 68ch; }

                /* Footer action band — the VatStepNavigation bottom band on
                   the modern-card grey scale. */
                .ye-step-footer {
                    background: #f9fafb;
                    border-top: 1px solid #e5e7eb;
                    padding: 1rem 1.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 0.75rem;
                }
                .ye-footer-hint { color: #6b7280; font-size: 0.8125rem; }
                .ye-footer-done {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    color: #166534;
                    font-weight: 600;
                    font-size: 0.875rem;
                }

                /* Status badges — the VatModernHeader .vat-badge family. */
                .ye-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.375rem;
                    padding: 0.25rem 0.625rem;
                    border-radius: 16px;
                    border: 1px solid transparent;
                    font-size: 0.75rem;
                    font-weight: 600;
                    line-height: 1.4;
                    white-space: nowrap;
                }
                .ye-badge-success { background: #f0fdf4; color: #166534; border-color: #bbf7d0; }
                .ye-badge-warning { background: #fffbeb; color: #92400e; border-color: #fde68a; }
                .ye-badge-danger { background: #fef2f2; color: #dc2626; border-color: #fecaca; }
                .ye-badge-neutral { background: #f1f5f9; color: #475569; border-color: #e2e8f0; }
                .ye-badge-primary {
                    background: var(--rgba-primary-1);
                    color: var(--primary);
                    border-color: var(--rgba-primary-2);
                }

                /* Muted info well — the app's .bg-light/.modern-table-container
                   grey box treatment. */
                .ye-well {
                    background: #f9fafb;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.625rem 0.875rem;
                    font-size: 0.8125rem;
                    color: #4b5563;
                    margin-top: 0.5rem;
                }
                .ye-well ul { margin: 0.25rem 0 0 0; padding-left: 1.1rem; }

                /* ── Shared step chrome ─────────────────────────────────
                   Form fields, section headings, label/value rows,
                   proposal bands and inline confirms are used by several
                   steps (adjustments, tax, annual report, stämma, post-
                   stämma) — but only the DISPLAYED step's <style> block is
                   mounted, so the shared pieces live here. Step-local
                   duplicates are harmless (identical rules). */
                .ye-section-heading {
                    font-size: 0.78rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    color: #6b7280;
                    margin: 0 0 0.6rem;
                }
                .ye-form-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.75rem 1rem;
                }
                .ye-field { flex: 1 1 180px; max-width: 260px; }
                .ye-field-wide { flex: 2 1 320px; max-width: 480px; }
                .ye-field label {
                    display: block;
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: #4b5563;
                    margin-bottom: 0.3rem;
                }
                .ye-field input, .ye-field textarea, .ye-field select {
                    width: 100%;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.45rem 0.75rem;
                    font-size: 0.875rem;
                    background: #fff;
                    transition: border-color 0.2s ease, box-shadow 0.2s ease;
                }
                .ye-field input:focus, .ye-field textarea:focus, .ye-field select:focus {
                    outline: none;
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1);
                }
                .ye-field input:disabled, .ye-field textarea:disabled, .ye-field select:disabled {
                    background: #f9fafb;
                    color: #6b7280;
                }
                .ye-field-help { font-size: 0.75rem; color: #9ca3af; margin-top: 0.25rem; }
                .ye-proposal { margin-top: 1rem; }
                .ye-proposal-head {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                }
                .ye-proposal-title { font-size: 0.875rem; color: var(--title, #374151); }
                .ye-proposal-actions {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                    margin-top: 1rem;
                }
                .ye-statements-toggle {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                    background: none;
                    border: none;
                    padding: 1rem 1.25rem;
                    font-size: 0.9375rem;
                    font-weight: 600;
                    color: var(--title, #374151);
                    cursor: pointer;
                }
                .ye-statements-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1.5rem;
                    align-items: start;
                }
                @media (max-width: 991px) {
                    .ye-statements-grid { grid-template-columns: 1fr; }
                }
                .ye-ink2-question {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1rem;
                    padding: 0.5rem 0;
                    font-size: 0.875rem;
                    max-width: 640px;
                }
                .ye-spin { animation: ye-rotate 1s linear infinite; }
                @keyframes ye-rotate { to { transform: rotate(360deg); } }

                /* Sektionsband — används av post-stämma, INK2-, EF- och
                   NE-stegen. Låg tidigare i PostStammaStep-blocket, som bara
                   monteras när DET steget visas: på deklarationsstegen föll
                   all sektionsseparation bort och sidan blev en textvägg.
                   Delade regler hör hemma här (se blockets ingress). */
                .ye-ps-section + .ye-ps-section {
                    margin-top: 1.75rem;
                    padding-top: 1.5rem;
                    border-top: 1px solid #f3f4f6;
                }

                .ye-tax-rows { max-width: 560px; }
                .ye-tax-row {
                    display: flex;
                    justify-content: space-between;
                    gap: 1rem;
                    padding: 0.35rem 0;
                    font-size: 0.875rem;
                    color: var(--title, #374151);
                }
                .ye-tax-row-group {
                    border-top: 1px solid #f3f4f6;
                    margin-top: 0.25rem;
                    padding-top: 0.5rem;
                    font-weight: 600;
                }
                .ye-tax-row .ye-num {
                    white-space: nowrap;
                    font-variant-numeric: tabular-nums;
                }
                .ye-inline-confirm {
                    display: flex;
                    flex-direction: column;
                    gap: 0.6rem;
                    margin-top: 0.75rem;
                    padding: 0.75rem 1rem;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    font-size: 0.8125rem;
                    color: #4b5563;
                    max-width: 560px;
                }
                .ye-inline-confirm-actions {
                    display: flex;
                    gap: 0.5rem;
                    flex-wrap: wrap;
                }
                .ye-icon-btn {
                    background: none;
                    border: none;
                    padding: 2px;
                    color: #6b7280;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                }
                .ye-icon-btn:hover { color: var(--primary); }

                .ye-year-select { min-width: 300px; }
                .ye-phase-intro {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                    background: var(--rgba-primary-1);
                    border: 1px solid var(--rgba-primary-3, var(--rgba-primary-1));
                    border-radius: 10px;
                    padding: 0.9rem 1.25rem;
                    margin-bottom: 1rem;
                    font-size: 0.875rem;
                    color: var(--title, #374151);
                    line-height: 1.55;
                }
                .ye-phase-intro-title {
                    font-size: 0.72rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: var(--primary);
                }
                .ye-step-counter {
                    text-align: center;
                    font-size: 0.75rem;
                    color: #9ca3af;
                    margin-bottom: 2rem;
                }
                @media (max-width: 768px) {
                    .ye-year-select { min-width: 0; width: 100%; }
                }
                .ye-error-help { background: none; border: none; padding: 0 2px; margin-left: 4px; color: var(--bs-secondary-color, #6c757d); vertical-align: text-bottom; cursor: help; }
                .ye-error-help:hover, .ye-error-help:focus-visible { color: var(--primary); outline: none; }
            `}</style>

            <div className="ye-page-container">
                {/* Header: title + fiscal-year selector */}
                <div className="modern-card">
                    <div className="modern-card-header ye-step-header">
                        <div>
                            <h3 className="section-title">
                                <CalendarCheck size={20} />
                                Bokslut
                                {process && STATUS_LABELS[process.status] && (
                                    <span className={`ye-badge ye-badge-${STATUS_VARIANTS[process.status] || 'primary'}`}>
                                        {STATUS_LABELS[process.status]}
                                    </span>
                                )}
                            </h3>
                            <p className="section-subtitle">
                                Avsluta räkenskapsåret steg för steg — i din egen takt.
                            </p>
                        </div>
                        <div className="ye-year-select">
                            <Select
                                theme={brandSelectTheme}
                                options={yearOptions}
                                value={selectedOption}
                                isLoading={loadingYears}
                                placeholder="Välj räkenskapsår…"
                                noOptionsMessage={() => 'Inga räkenskapsår'}
                                onChange={(option) => {
                                    if (option && String(option.value) !== String(fyParam)) {
                                        history.push(`/${companyId}/year-end/${option.value}`);
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: 260 }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Laddar…</span>
                        </div>
                    </div>
                ) : yearsError ? (
                    <div className="alert alert-danger">{yearsError}</div>
                ) : fiscalYears.length === 0 ? (
                    <div className="modern-card">
                        <div className="modern-card-body">
                            <p className="text-muted mb-0">
                                Det finns inga räkenskapsår upplagda ännu. Lägg upp ett räkenskapsår
                                under Inställningar → Bokföring innan du börjar med bokslutet.
                            </p>
                        </div>
                    </div>
                ) : forbidden ? (
                    <div className="modern-card">
                        <div className="modern-card-body">
                            <p className="text-muted mb-0">
                                Bokslutet kan bara hanteras av företagets administratörer. Be en
                                administratör om hjälp, eller om att ge dig rätt behörighet.
                            </p>
                        </div>
                    </div>
                ) : overviewError ? (
                    <div className="modern-card">
                        <div className="modern-card-body">
                            <EmptyState
                                icon={CalendarX2}
                                title="Bokslutet kan inte visas"
                                description={(
                                    <>
                                        {overviewError.message}
                                        {overviewError.explanation && (
                                            /* Frågetecken-hover, samma mönster som
                                               NE-bilagans InfoHover: detaljerna vid
                                               hovring i stället för en textvägg. */
                                            <OverlayTrigger
                                                trigger={['hover', 'focus']}
                                                placement="top"
                                                overlay={(
                                                    <Popover id="ye-overview-error-help" style={{ maxWidth: 360 }}>
                                                        <Popover.Body style={{ fontSize: '0.8125rem', lineHeight: 1.55 }}>
                                                            {overviewError.explanation}
                                                        </Popover.Body>
                                                    </Popover>
                                                )}
                                            >
                                                <button type="button" className="ye-error-help" aria-label="Varför?">
                                                    <HelpCircle size={15} />
                                                </button>
                                            </OverlayTrigger>
                                        )}
                                    </>
                                )}
                                actions={overviewError.retryable ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() => fetchOverview()}
                                    >
                                        Försök igen
                                    </button>
                                ) : null}
                            />
                        </div>
                    </div>
                ) : !overview ? null : !process ? (
                    <YearEndIntroCard
                        fiscalYear={selectedFiscalYear || overview?.readiness?.fiscalYear}
                        eligibility={overview?.eligibility}
                        onStart={handleStart}
                        starting={starting}
                    />
                ) : (
                    <>
                        <YearEndProgressTracker
                            stepOrder={shownSteps}
                            displayedStep={displayedStep}
                            isStepCompleted={isStepCompleted}
                            onStepClick={(step) => setViewingStep(step === currentStep ? null : step)}
                            processType={process?.process_type}
                        />

                        {(() => {
                            // Fasövergångsbanner: när användaren står på första
                            // steget i en ny fas (och inte förhandsvisar) —
                            // ögonblicket "bokslutet är klart, nu börjar
                            // årsredovisningen" som en enda stegkedja annars döljer.
                            if (isPreviewing) return null;
                            const phase = stepPhase(displayedStep);
                            if (phase === PhaseEnum.BOKSLUT || !PHASE_INTROS[phase]) return null;
                            const firstOfPhase = shownSteps.find((s) => stepPhase(s) === phase);
                            if (displayedStep !== firstOfPhase || isStepCompleted(displayedStep)) return null;
                            return (
                                <div className="ye-phase-intro">
                                    <span className="ye-phase-intro-title">{PHASE_TITLES[phase]}</span>
                                    <span>{PHASE_INTROS[phase]}</span>
                                </div>
                            );
                        })()}

                        {isPreviewing && (
                            <div
                                className="d-flex align-items-center justify-content-between px-3 py-2 mb-2"
                                style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: '0.85rem' }}
                            >
                                <span style={{ color: '#1d4ed8' }}>
                                    Förhandsvisning av <strong>{stepTitle(displayedStep, process?.process_type)}</strong>
                                </span>
                                <button
                                    onClick={() => setViewingStep(null)}
                                    style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
                                >
                                    ← Tillbaka till {stepTitle(currentStep, process?.process_type)}
                                </button>
                            </div>
                        )}

                        <div className="modern-card">
                            {renderStepContent()}
                        </div>

                        {process?.process_id && displayedStep !== StepEnum.PREFLIGHT && (
                            <StatementsPanel
                                companyId={companyId}
                                processId={process.process_id}
                                processType={process.process_type}
                            />
                        )}

                        <div className="ye-step-counter">
                            Steg {shownSteps.indexOf(displayedStep) + 1} av {shownSteps.length}
                            {' · '}{PHASE_TITLES[stepPhase(displayedStep)]}
                            {PHASE_DESCRIPTIONS[stepPhase(displayedStep)]
                                ? ` — ${PHASE_DESCRIPTIONS[stepPhase(displayedStep)]}`
                                : ''}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default YearEndPage;
